import { matchPath } from 'react-router-dom';

export interface RouteTitleEntry {
  path: string;
  title: string;
}

const SUFFIX = 'KidsBooked Pro';

/**
 * KB-2619: route → document title map. One place to add a title when a
 * route is added to `src/App.tsx` — do not scatter per-page
 * `document.title` effects again (that's what caused every tab and every
 * GA4 `page_title` to collapse to one string).
 *
 * Declared in the same order as `App.tsx`'s routes — most-specific first,
 * the `*` fallback last. Matching below is sequential (first match wins),
 * not React Router's specificity-scored match, so `*` must stay last. Every
 * other pair is disjoint under an exact (`end: true`) match, so their
 * relative order doesn't matter — kept parallel to `App.tsx` anyway, for
 * anyone diffing the two files.
 */
export const ROUTE_TITLES: RouteTitleEntry[] = [
  { path: '/logout', title: `Signing Out · ${SUFFIX}` },
  { path: '/login', title: `Log In · ${SUFFIX}` },
  { path: '/create-account', title: `Create Account · ${SUFFIX}` },
  { path: '/forgot-password', title: `Forgot Password · ${SUFFIX}` },
  { path: '/request-account', title: `Request Account · ${SUFFIX}` },
  { path: '/welcome', title: `Welcome · ${SUFFIX}` },
  { path: '/terms-of-use', title: `Terms of Use · ${SUFFIX}` },
  { path: '/', title: SUFFIX },
  { path: '/dashboard', title: `Dashboard · ${SUFFIX}` },
  { path: '/profile', title: `Profile · ${SUFFIX}` },
  { path: '/organizations/:id/edit/locations/details', title: `Organization Locations · ${SUFFIX}` },
  { path: '/organizations/:id/edit', title: `Edit Organization · ${SUFFIX}` },
  { path: '/organizations/:id/support', title: `Organization Support · ${SUFFIX}` },
  { path: '/organizations', title: `Organizations · ${SUFFIX}` },
  { path: '/locations/:id/edit', title: `Edit Location · ${SUFFIX}` },
  { path: '/locations', title: `Locations · ${SUFFIX}` },
  { path: '/offers/:id/edit', title: `Edit Offer · ${SUFFIX}` },
  { path: '/offers', title: `Offers · ${SUFFIX}` },
  { path: '/users/:id/digest-preview', title: `Digest Preview · ${SUFFIX}` },
  { path: '/users/:id', title: `User Detail · ${SUFFIX}` },
  { path: '/users', title: `Users · ${SUFFIX}` },
  { path: '/account-requests', title: `Camp manager account requests · ${SUFFIX}` },
  { path: '/admin/metrics-health', title: `Metrics Health · ${SUFFIX}` },
  { path: '/admin/traffic-reconciliation', title: `Traffic Reconciliation · ${SUFFIX}` },
  { path: '/admin-reports', title: `Admin Reports · ${SUFFIX}` },
  { path: '/graphs', title: `Graphs · ${SUFFIX}` },
  { path: '/treemap', title: `Org Locations Treemap · ${SUFFIX}` },
  { path: '/fields-options/sortable-options/:id', title: `Edit Sortable Option · ${SUFFIX}` },
  { path: '/fields-options/decimal-enums/:id', title: `Edit Decimal Enum · ${SUFFIX}` },
  { path: '/fields-options', title: `Fields & Options · ${SUFFIX}` },
  { path: '/email-templates/enqueued', title: `Enqueued Emails · ${SUFFIX}` },
  { path: '/email-templates/:id', title: `Email Template · ${SUFFIX}` },
  { path: '/email-templates', title: `Email Templates · ${SUFFIX}` },
  { path: '/email-wrappers/:id', title: `Email Wrapper · ${SUFFIX}` },
  { path: '/email-wrappers', title: `Email Wrappers · ${SUFFIX}` },
  { path: '/system-emails/:id', title: `Campaign · ${SUFFIX}` },
  { path: '/system-emails', title: `Campaigns · ${SUFFIX}` },
  { path: '/comms', title: `Comms · ${SUFFIX}` },
  { path: '/outreach/:id', title: `Outreach Campaign · ${SUFFIX}` },
  { path: '/outreach', title: `Outreach · ${SUFFIX}` },
  { path: '/settings', title: `Settings · ${SUFFIX}` },
  { path: '/debug-user', title: `Debug User · ${SUFFIX}` },
  { path: '/support/create', title: `Create Support Case · ${SUFFIX}` },
  { path: '/support/:id', title: `Support Case · ${SUFFIX}` },
  { path: '/support', title: `Support Cases · ${SUFFIX}` },
  { path: '/camp-data', title: `Camp Data · ${SUFFIX}` },
  { path: '/admin/my-group-viewer', title: `My Group Viewer · ${SUFFIX}` },
  { path: '/admin/group-sheets', title: `Group Sheets · ${SUFFIX}` },
  { path: '/admin/camp-ingest-ledger', title: `Camp Approval Ledgers · ${SUFFIX}` },
  { path: '/admin/camp-ingest-ledger/:ledgerId', title: `Camp Approval Ledger · ${SUFFIX}` },
  { path: '/admin/camp-ingest-ledger/:ledgerId/audit', title: `Ledger Decision Audit · ${SUFFIX}` },
  { path: '*', title: `Not Found · ${SUFFIX}` },
];

const DEFAULT_TITLE = SUFFIX;

/**
 * Resolves a pathname to its document title. The trailing `*` entry in
 * `ROUTE_TITLES` matches everything, so `DEFAULT_TITLE` is a belt-and-braces
 * fallback for a caller that passes something matchPath can't parse at all —
 * it should never be reachable through normal navigation.
 */
export function getTitleForPath(pathname: string): string {
  for (const entry of ROUTE_TITLES) {
    if (matchPath({ path: entry.path, end: true }, pathname)) {
      return entry.title;
    }
  }
  return DEFAULT_TITLE;
}
